import { memo, useState, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ThumbSvg, ThumbedSvg, TreadSvg, TreadedSvg, FeedbackSvg, CopySvg } from '@/components/svg-icons/action-bar';
import FeedbackModal from './feedback-modal';
import queryDomMethods from '@/utils/query-dom-methods';
import { toastInQa } from '@/utils/qa';
import { copyToClipboard } from '@/utils';
import classNames from 'classnames';
import './index.scss';

export interface IActionBarRef {
    show: () => void;
    hide: () => void;
}

interface IProps {
    recordId: string;
    content: string;
    /**
     * 是否常驻
     */
    isPermanent?: boolean;
    feedbackType?: 1 | 2;
    isFeedbackEvaluation?: boolean;
    onThumbOrTread: (feedbackType: 1 | 2) => void;
    onFeedback: (params: { labels: number[]; content: string }) => void;
}

const ActionBar = ({ isPermanent, content, feedbackType, isFeedbackEvaluation, onThumbOrTread, onFeedback }: IProps, ref: any) => {
    const [hoverVisible, setHoverVisible] = useState(false);
    const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
    const timer = useRef<ReturnType<typeof setTimeout>>();

    const visible = useMemo(() => {
        if (isPermanent) return true;
        return hoverVisible;
    }, [hoverVisible, isPermanent]);

    const handleShow = () => {
        if (isPermanent) return;
        clearTimeout(timer.current);
        setHoverVisible(true);
    };

    const handleHide = (immediately = false) => {
        if (isPermanent) return;
        if (immediately) {
            setHoverVisible(false);
        } else {
            timer.current = setTimeout(() => setHoverVisible(false), 100);
        }
    };

    const handleThumb = () => {
        handleHide(true);
        onThumbOrTread(1);
    };

    const handleTread = () => {
        handleHide(true);
        onThumbOrTread(2);
    };

    const handleOpenFeedbackModal = () => {
        handleHide(true);
        setFeedbackModalVisible(true);
    };

    const handleCloseFeedbackModal = () => setFeedbackModalVisible(false);

    const handleCopy = () => {
        handleHide(true);
        copyToClipboard(content);
        toastInQa('复制成功', 'success');
    };

    useImperativeHandle(
        ref,
        () => ({
            show: handleShow,
            hide: handleHide,
        }),
        [isPermanent]
    );

    return (
        <>
            {(visible || feedbackModalVisible) &&
                createPortal(
                    <FeedbackModal visible={feedbackModalVisible} onClose={handleCloseFeedbackModal} onSumbit={onFeedback} />,
                    queryDomMethods.querySelector('.qa')
                )}
            {visible && (
                <div
                    className={classNames('action-bar', isPermanent && 'action-bar--permanent')}
                    onMouseEnter={handleShow}
                    onMouseLeave={() => handleHide()}
                >
                    {feedbackType === undefined && (
                        <>
                            <ThumbSvg className="action-bar__thumb" onClick={handleThumb} />
                            <div className="action-bar__line" />
                            <TreadSvg className="action-bar__tread" onClick={handleTread} />
                        </>
                    )}
                    {feedbackType === 1 && <ThumbedSvg />}
                    {feedbackType === 2 && <TreadedSvg />}
                    <div className="action-bar__line" />
                    {!isFeedbackEvaluation && (
                        <>
                            <FeedbackSvg className="action-bar__feedback" onClick={handleOpenFeedbackModal} />
                            <div className="action-bar__line" />
                        </>
                    )}
                    <CopySvg className="action-bar__copy" onClick={handleCopy} />
                </div>
            )}
        </>
    );
};

export default memo(forwardRef(ActionBar));
